import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Sparkles, Loader2, AlertTriangle, Plus, Paperclip, FolderOpen, Trash2, Send, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { NarrativeDrawer } from './NarrativeDrawer';
import { draftStartMessage } from './draftMessage';
import { AttachmentLibraryDialog } from './AttachmentLibraryDialog';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLE = { todo: 'bg-slate-100 text-slate-500 border-slate-200', in_progress: 'bg-blue-50 text-blue-700 border-blue-200', done: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
const STATUS_LABEL = { todo: 'To do', in_progress: 'In progress', done: 'Done' };

const EmptyShell = ({ what }) => (
  <p className="text-sm text-slate-400 py-10 text-center" data-testid="rfp-tab-empty">
    No {what} yet — load the client's RFP documents on the <b>Container</b> tab and build the response shell.
  </p>
);

function AddRow({ pid, type, placeholder, onAdded, testId }) {
  const [title, setTitle] = useState('');
  const add = async () => {
    if (!title.trim()) return;
    try { await axios.post(`${API_URL}/api/rfp/response/projects/${pid}/deliverables`, { type, title }, { withCredentials: true }); setTitle(''); onAdded(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not add'); }
  };
  return (
    <div className="flex items-center gap-2">
      <Input value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} placeholder={placeholder} className="h-8 text-xs max-w-md" data-testid={`${testId}-title`} />
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={add} data-testid={`${testId}-btn`}><Plus className="w-3 h-3 mr-1" />Add</Button>
    </div>
  );
}

/** Narratives tab — Word-style responses (narrative sections + generated letters). */
export function NarrativesPanel({ project, assignees = [], onRefresh, onOpenReviewers, focus }) {
  const pid = project.id;
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isLeader = ['admin', 'super_admin', 'cro', 'cco'].includes(user?.role);
  const setReviewer = async (d, reviewer) => {
    try { await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}/reviewer`, { reviewer }, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not set reviewer'); }
  };
  const signOff = async (d, revoke = false) => {
    try {
      await axios.post(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}/signoff`, {}, { params: revoke ? { revoke: true } : {}, withCredentials: true });
      toast.success(revoke ? 'Sign-off revoked' : `"${d.title}" signed off`); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not sign off'); }
  };
  const items = (project.deliverables || []).filter(d => d.type === 'narrative' || d.type === 'generated');
  const [openId, setOpenId] = useState(null);
  useEffect(() => { if (focus?.did) setOpenId(focus.did); }, [focus]);
  const [digesting, setDigesting] = useState(false);
  const current = openId ? items.find(x => x.id === openId) : null;
  const patch = async (d, body) => {
    try { await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}`, body, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };
  const draft = async (d) => {
    try { const r = await axios.post(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}/draft`, {}, { withCredentials: true }); toast.success(draftStartMessage(r.data), { duration: 7000 }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not start drafting'); }
  };
  const remove = async (d) => {
    const n = (d.sections || []).length;
    if (!window.confirm(`Remove the narrative response group "${d.title}"${n ? ` and its ${n} section${n === 1 ? '' : 's'}` : ''}?\n\nA version of the response is saved first, so this can be restored from the History tab.`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}`, { withCredentials: true }); toast.success(`"${d.title}" removed — restorable from History`); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };
  if (!(project.deliverables || []).length) {
    return (
      <div className="space-y-3" data-testid="rfp-narratives-empty">
        <EmptyShell what="narrative responses" />
        <AddRow pid={pid} type="narrative" placeholder="…or add a narrative response group by hand (e.g. Annex M – Project Requirements)" onAdded={onRefresh} testId="rfp-narr-add" />
      </div>
    );
  }
  const totalSecs = items.reduce((n, d) => n + (d.sections || []).length, 0);
  const drafted = items.reduce((n, d) => n + (d.sections || []).filter(s => (s.draft || '').trim()).length, 0);
  const awaiting = items.filter(d => d.reviewer && !d.signoff).length;
  const sendDigest = async () => {
    setDigesting(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/reviewer-digest/send`, {}, { params: { force: true }, withCredentials: true });
      toast.success(r.data.reviewers ? `Digest sent to ${r.data.reviewers} reviewer${r.data.reviewers === 1 ? '' : 's'} (${r.data.documents} document${r.data.documents === 1 ? '' : 's'}, ${r.data.emails} email${r.data.emails === 1 ? '' : 's'})` : 'No narrative documents are awaiting sign-off');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send digest'); }
    finally { setDigesting(false); }
  };
  return (
    <div className="space-y-3" data-testid="rfp-narratives-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Narrative responses</span>
        <Badge variant="outline" className="text-[9px]" data-testid="rfp-narratives-progress">{drafted} of {totalSecs} sections drafted</Badge>
        {awaiting > 0 && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="rfp-narratives-awaiting">{awaiting} awaiting sign-off</Badge>}
        <span className="text-[10px] text-slate-500">Word-style answers drafted with AI — a close Narrative Library block is tailored to this client first, the Capability Library covers the rest — then edited and exported per document or inside the submission package</span>
        {isAdmin && (
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={sendDigest} disabled={digesting}
            title="Reviewers get this list daily at 08:00 UTC — send it now" data-testid="rfp-reviewer-digest-btn">
            {digesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send reviewer digest
          </Button>
        )}
        {isLeader && onOpenReviewers && (
          <Button size="sm" variant="outline" className={`h-7 text-xs ${isAdmin ? '' : 'ml-auto'}`} onClick={onOpenReviewers}
            title="Every reviewer's pending narratives across projects — reassign in one click" data-testid="rfp-reviewers-btn">
            <Users className="w-3.5 h-3.5 mr-1" />Reviewers
          </Button>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map(d => {
          const secs = d.sections || [];
          const dn = secs.filter(s => (s.draft || '').trim()).length;
          const gaps = secs.filter(s => (s.draft || '').includes('[GAP')).length;
          const done = secs.filter(s => s.status === 'done').length;
          return (
            <div key={d.id} className="border border-slate-200 bg-white rounded-md p-3 space-y-2" data-testid={`rfp-narr-${d.id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0F2040] truncate">{d.title}</p>
                  <p className="text-[10px] text-slate-500">{d.type === 'generated' ? 'Generated from RFP facts' : `${secs.length} sections from the client's document`}{d.note && d.type === 'generated' ? ` · ${d.note}` : ''}</p>
                </div>
                <Badge variant="outline" className={`text-[9px] shrink-0 ${STATUS_STYLE[d.status]}`}>{STATUS_LABEL[d.status]}</Badge>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#4ABFCF]" style={{ width: `${secs.length ? Math.round(dn / secs.length * 100) : 0}%` }} /></div>
              <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500">
                <span>{dn}/{secs.length} drafted · {done} done</span>
                {gaps > 0 && <span className="text-amber-600 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{gaps} gap{gaps === 1 ? '' : 's'}</span>}
                {d.drafting && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200"><Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />{d.progress?.label || 'Drafting…'}</Badge>}
                <select value={d.owner || ''} onChange={e => patch(d, { owner: e.target.value })} className="ml-auto h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-600 max-w-[150px]" data-testid={`rfp-narr-owner-${d.id}`}>
                  <option value="">Owner…</option>{assignees.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-[10px]" data-testid={`rfp-narr-review-${d.id}`}>
                <span className="text-slate-500">Reviewer</span>
                <select value={d.reviewer || ''} onChange={e => setReviewer(d, e.target.value)} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-600 max-w-[170px]" data-testid={`rfp-narr-reviewer-${d.id}`}>
                  <option value="">Name a reviewer…</option>{assignees.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                </select>
                {d.signoff ? (
                  <>
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title={`Signed off ${new Date(d.signoff.at).toLocaleString()}${d.signoff.as_admin ? ' (admin override)' : ''}`} data-testid={`rfp-narr-signed-${d.id}`}>✓ signed off by {d.signoff.by}</Badge>
                    {(isAdmin || user?.email?.toLowerCase() === d.reviewer?.toLowerCase()) && <button type="button" onClick={() => signOff(d, true)} className="text-slate-400 hover:text-rose-500" data-testid={`rfp-narr-revoke-${d.id}`}>revoke</button>}
                  </>
                ) : d.reviewer ? (
                  (isAdmin || user?.email?.toLowerCase() === d.reviewer.toLowerCase())
                    ? <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-emerald-700 border-emerald-200" onClick={() => signOff(d)} data-testid={`rfp-narr-signoff-${d.id}`}>Sign off</Button>
                    : <span className="text-amber-600" data-testid={`rfp-narr-awaiting-${d.id}`}>awaiting {d.reviewer}</span>
                ) : <span className="text-amber-600">needed before packaging</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" className="h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" onClick={() => setOpenId(d.id)} data-testid={`rfp-narr-open-${d.id}`}>{dn ? 'Open editor' : 'Write'}</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={d.drafting || dn === secs.length} onClick={() => draft(d)} data-testid={`rfp-narr-draft-${d.id}`}><Sparkles className="w-3 h-3 mr-1" />Draft with AI</Button>
                <a href={`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}/export/docx`} className="text-[11px] text-[#4ABFCF] hover:underline ml-1" data-testid={`rfp-narr-word-${d.id}`}>Word</a>
                <button type="button" onClick={() => remove(d)} className="ml-auto text-slate-300 hover:text-rose-500" title="Remove" data-testid={`rfp-narr-del-${d.id}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          );
        })}
      </div>
      <AddRow pid={pid} type="narrative" placeholder="Add a narrative response group (e.g. Annex M – Project Requirements)" onAdded={onRefresh} testId="rfp-narr-add" />
      <NarrativeDrawer project={project} deliverable={current} open={!!current} onOpenChange={o => { if (!o) setOpenId(null); }} onChanged={onRefresh} focusSectionId={focus?.did === current?.id ? focus?.sid : null} assignees={assignees} />
    </div>
  );
}

/** Attachments tab — forms to return and standard documents from the library. */
export function AttachmentsPanel({ project, assignees = [], onRefresh }) {
  const pid = project.id;
  const items = (project.deliverables || []).filter(d => d.type === 'attachment' || d.type === 'form');
  const [pickFor, setPickFor] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const patch = async (d, body) => {
    try { await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}`, body, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${d.id}`, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not remove'); }
  };
  const libraryBtn = (
    <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px]" onClick={() => { setPickFor(null); setLibOpen(true); }} data-testid="rfp-attachments-library-btn"><FolderOpen className="w-3 h-3 mr-1" />Attachment library</Button>
  );
  const libraryDialog = (
    <AttachmentLibraryDialog open={libOpen} onOpenChange={setLibOpen} title={pickFor ? `Attach to “${pickFor.title}”` : 'Attachment library'}
      onPick={pickFor ? async (it) => { await patch(pickFor, { attachment: { library_id: it.id } }); setLibOpen(false); setPickFor(null); toast.success(`"${it.name}" attached`); } : null} />
  );
  if (!(project.deliverables || []).length) {
    return (
      <div className="space-y-3" data-testid="rfp-attachments-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <Paperclip className="w-4 h-4 text-[#4ABFCF]" />
          <span className="text-sm font-semibold text-[#0F2040]">Attachments &amp; client forms</span>
          <span className="text-[10px] text-slate-500">The shared library (uploads + Google Drive) is available before the response shell exists</span>
          {libraryBtn}
        </div>
        <EmptyShell what="attachments" />
        {libraryDialog}
      </div>
    );
  }
  const attached = items.filter(d => d.attachment).length;
  return (
    <div className="space-y-3" data-testid="rfp-attachments-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Paperclip className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Attachments &amp; client forms</span>
        <Badge variant="outline" className="text-[9px]" data-testid="rfp-attachments-progress">{attached} of {items.length} attached</Badge>
        {libraryBtn}
      </div>
      <div className="divide-y divide-slate-100 border border-slate-200 bg-white rounded-md">
        {items.length === 0 && <p className="text-xs text-slate-400 p-4">No attachment or form items in the checklist yet.</p>}
        {items.map(d => (
          <div key={d.id} className="p-2.5 flex items-center gap-2 text-xs flex-wrap" data-testid={`rfp-att-${d.id}`}>
            <Badge variant="outline" className={`text-[9px] ${STATUS_STYLE[d.status]}`}>{STATUS_LABEL[d.status]}</Badge>
            <Badge variant="outline" className="text-[9px] capitalize">{d.type}</Badge>
            <span className="font-medium text-[#0F2040]">{d.title}</span>
            {d.attachment ? (
              <a href={`${API_URL}/api/rfp/files/${d.attachment.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#4ABFCF] hover:underline" data-testid={`rfp-att-file-${d.id}`}><Paperclip className="w-3 h-3" />{d.attachment.name}</a>
            ) : <span className="text-[10px] text-slate-400 truncate max-w-[380px]" title={d.note}>{d.note}</span>}
            <span className="ml-auto flex items-center gap-1.5">
              <select value={d.owner || ''} onChange={e => patch(d, { owner: e.target.value })} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-600 max-w-[150px]" data-testid={`rfp-att-owner-${d.id}`}>
                <option value="">Owner…</option>{assignees.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
              </select>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => { setPickFor(d); setLibOpen(true); }} data-testid={`rfp-att-attach-${d.id}`}><Paperclip className="w-3 h-3 mr-1" />{d.attachment ? 'Change' : 'Attach'}</Button>
              {d.attachment && <button type="button" onClick={() => patch(d, { attachment: {}, status: 'todo' })} className="text-[10px] text-slate-400 hover:text-rose-500" data-testid={`rfp-att-clear-${d.id}`}>clear</button>}
              <select value={d.status} onChange={e => patch(d, { status: e.target.value })} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-600" data-testid={`rfp-att-status-${d.id}`}>
                {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button type="button" onClick={() => remove(d)} className="text-slate-300 hover:text-rose-500" title="Remove" data-testid={`rfp-att-del-${d.id}`}><Trash2 className="w-3 h-3" /></button>
            </span>
          </div>
        ))}
      </div>
      <AddRow pid={pid} type="attachment" placeholder="Add an attachment item (e.g. ISO 27001 certificate)" onAdded={onRefresh} testId="rfp-att-add" />
      {libraryDialog}
    </div>
  );
}
